import axios from 'axios'
import * as HttpStatus from 'http-status-codes'
import { Address as AddressWithoutId } from 'shared/components/Address/AddressFields'

import { getVGSDomain } from '../config/config'
import { Address, Team, User } from '.'
import { CreditApplication } from './cashSummary'

export interface BillingPeriod {
  id: string
  startDate: string
  endDate: string
  isBillingPeriodClosed: boolean
}

export enum LedgerType {
  CHARGE_CARD_BALANCE = 'CHARGE_CARD_BALANCE',
  AVAILABLE_BALANCE = 'AVAILABLE_BALANCE',
}

export interface Card {
  id: string
  datePosted: string
  dateUpdated: string
  expMonth: number
  expYear: number
  last4: string
  billingAddress?: Address
  shippingAddress: Address
  status: 'active' | 'inactive' | 'canceled'
  team: Team['id']
  type: 'physical' | 'virtual'
  user: User['id']
  cardholderName: string
  everActivated: boolean
  shippingCarrier: string
  shippingTrackingNumber: string
  shippingTrackingUrl: string
  pinStatus: string
  fundingSource: LedgerType
}

export interface CardPayload {
  id: string
  date_posted: string
  date_updated: string
  exp_month: number
  exp_year: number
  last4: string
  billing_address?: Address
  shipping_address: Address
  status: Card['status']
  team: Team['id']
  type: Card['type']
  user: User['id']
  cardholder_name: string
  ever_activated: boolean
  shipping_carrier: string
  shipping_tracking_number: string
  shipping_tracking_url: string
  pin_status: string
  funding_source: LedgerType
}

export interface Balance {
  active: boolean
  availableAmount: number
  cardType: 'debit' | 'credit'
  ledgerId: number
}

export interface Balances {
  balances: Balance[]
}

export enum BillingStatus {
  OVERDUE = 'OVERDUE',
  PENDING = 'PENDING',
  RECEIVED = 'RECEIVED',
  SCHEDULED = 'SCHEDULED',
}

export interface Billing {
  currentBilling?: BillingRecord
  previousBilling?: BillingRecord
}

export interface BillingRecord {
  billingCycleStartDate: string
  billingCycleStartDateObject: Date
  billingCycleEndDate: string
  billingCycleEndDateObject: Date
  amountOwed: number
  amountInflight: number
  billingStatus: BillingStatus
  scheduledDate: string
  scheduledDateObject: Date
}

export function parseCard(card: CardPayload): Card {
  return {
    id: card.id,
    datePosted: card.date_posted,
    dateUpdated: card.date_updated,
    expMonth: card.exp_month,
    expYear: card.exp_year,
    last4: card.last4,
    billingAddress: card.billing_address,
    shippingAddress: card.shipping_address,
    status: card.status,
    team: card.team,
    type: card.type,
    user: card.user,
    cardholderName: card.cardholder_name,
    everActivated: card.ever_activated,
    shippingCarrier: card.shipping_carrier,
    shippingTrackingNumber: card.shipping_tracking_number,
    shippingTrackingUrl: card.shipping_tracking_url,
    pinStatus: card.pin_status,
    fundingSource: card.funding_source,
  }
}

interface ActivateCardPayload {
  id: string
  exp_month: number
  exp_year: number
  last4: string
}

export interface CreditApplicationPayload {
  ssn_encrypted: string
  dob: string
  is_legal_owner: boolean
}

// Api

export function activateCard({ id, ...payload }: ActivateCardPayload): Promise<Card> {
  return axios.post(`/api/v1/cash/cards/${id}/activate/`, payload).then((response) => {
    if (response.status === HttpStatus.OK) {
      return parseCard(response.data)
    }
    throw response.data
  })
}

export function deactivateCard({ id }: { id: string }): Promise<Card> {
  return axios.post(`/api/v1/cash/cards/${id}/deactivate/`).then((response) => {
    if (response.status === HttpStatus.OK) {
      return parseCard(response.data)
    }
    throw response.data
  })
}

export function issueVirtualCard({ id }: { id: string; funding_source: LedgerType }): Promise<Card> {
  return axios.post(`/api/v1/cash/cards/issue-virtual-card/`, { user: id }).then((response) => {
    if (response.status === HttpStatus.OK) {
      return parseCard(response.data)
    }
    throw response.data
  })
}

export function createCreditApplication(application: CreditApplicationPayload): Promise<CreditApplication> {
  const url = `${getVGSDomain()}/api/v1/cash/credit-applications/submit/`
  return axios.post(url, application, { withCredentials: true }).then((response) => response.data)
}

export function completeCreditApplication(applicationId: number) {
  const url = `/api/v1/cash/credit-applications/${applicationId}/complete/`
  return axios.post(url)
}

export function cancelCreditApplication(applicationId: number) {
  const url = `/api/v1/cash/credit-applications/${applicationId}/cancel/`
  return axios.post(url)
}

export function clearCreditApplication() {
  const url = `/api/v1/cash/credit-applications/clear/`
  return axios.post(url)
}

export function declineCreditApplication(applicationId: number) {
  const url = `/api/v1/cash/credit-applications/${applicationId}/decline/`
  return axios.post(url)
}

export function optOutFromCredit() {
  const url = `/api/v1/cash/credit-applications/opt-out-from-credit/`
  return axios.post(url)
}

interface AcceptCreditApplicationUserPayload {
  user_id: string
  address: AddressWithoutId
}

interface AcceptCreditApplicationPayload {
  users?: AcceptCreditApplicationUserPayload[]
}

export function acceptCreditApplication(applicationId: number, payload?: AcceptCreditApplicationPayload) {
  const url = `/api/v1/cash/credit-applications/${applicationId}/accept/`
  return axios.post(url, payload)
}

interface BillingPeriodsPayload {
  teamId?: string
  dateStart?: string
  dateEnd?: string
}

export function fetchBillingPeriods({ dateStart, dateEnd }: BillingPeriodsPayload = {}): Promise<
  BillingPeriod[]
> {
  return axios
    .get(
      `/api/v1/cash/team-billing-infos/?${dateStart ? `date_start=${dateStart}` : ''}${
        dateEnd ? `${dateStart ? '&' : ''}date_end=${dateEnd}` : ''
      }`
    )
    .then((response) => {
      if (response.status === HttpStatus.OK) {
        return response.data.results.map((period: any) => ({
          id: period.id,
          startDate: period.start_date,
          endDate: period.end_date,
          isBillingPeriodClosed: period.is_billing_period_closed,
        }))
      }
      throw response.data
    })
}

export function fetchBalances(): Promise<Balances> {
  return axios.get('/api/v1/cash/balances/').then((response) => {
    if (response.status === HttpStatus.OK) {
      return {
        balances: response.data.balances.map((balance: any) => ({
          active: balance.active,
          availableAmount: balance.available_amount,
          cardType: balance.card_type,
          ledgerId: balance.ledeger_id,
        })),
      }
    }
    throw response.data
  })
}

export function fetchBilling(): Promise<Billing> {
  return axios.get('/api/v1/cash/billing/').then((response) => {
    const currentBilling = response.data.billing_records?.find(
      (b: any) => b.billing_status === BillingStatus.SCHEDULED
    )
    const previousBilling = response.data.billing_records?.find(
      (b: any) => b.scheduled_date !== currentBilling?.scheduled_date
    )

    return {
      currentBilling: currentBilling && parseBilling(currentBilling!),
      previousBilling: previousBilling && parseBilling(previousBilling),
    }
  })
}

function parseBilling(data: any) {
  return {
    billingCycleStartDate: data.billing_cycle_start_date,
    billingCycleStartDateObject: new Date(`${data.billing_cycle_start_date}T01:00:00-08:00`),
    billingCycleEndDate: data.billing_cycle_end_date,
    billingCycleEndDateObject: new Date(`${data.billing_cycle_end_date}T01:00:00-08:00`),
    amountOwed: data.amount_owed,
    amountInflight: data.amount_inflight,
    billingStatus: data.billing_status,
    scheduledDate: data.scheduled_date,
    scheduledDateObject: new Date(`${data.scheduled_date}T01:00:00-08:00`),
  }
}
